import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { signupApi } from '@/services/api';
import { SESSION_KEYS, EMAIL_VERIFICATION_POLL } from '@/config/constants';
import { Logo } from '@/components/Logo';
import { debug } from '@/utils/debug';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '@/components/ui';

interface LocationState {
  email?: string;
  memberUuid?: string;
  fromEmailChange?: boolean;
}

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'pending'>('verifying');
  const [message, setMessage] = useState('');
  const [pollingExpired, setPollingExpired] = useState(false);
  // Bumped on a successful resend to restart polling with a fresh deadline.
  const [pollingGeneration, setPollingGeneration] = useState(0);
  const verificationAttempted = useRef(false);

  const token = searchParams.get('token');
  const isFromEmailChange = locationState?.fromEmailChange;
  const pendingEmail = locationState?.email;
  const memberUuid = locationState?.memberUuid;

  // Poll for verification status while in 'pending' state (email-change flow).
  // Effect deps stay primitive so it doesn't tear down on every render — otherwise
  // setTimeout chain resets repeatedly and either storms the API or stops early.
  useEffect(() => {
    if (status !== 'pending' || !memberUuid) return;

    // Reset the expired flag whenever a fresh polling session starts so a
    // stale "haven't detected verification yet" banner from a prior cycle
    // doesn't carry over.
    setPollingExpired(false);

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const startTime = Date.now();

    const check = async () => {
      if (cancelled) return;
      try {
        const verificationStatus = await signupApi.checkEmailVerificationStatus(memberUuid);
        if (cancelled) return;
        debug.log('Email verification status:', verificationStatus);
        if (verificationStatus.email_verified) {
          setStatus('success');
          setMessage('Your new email address has been verified successfully.');
          toast.success('Email verified successfully!');
          return;
        }
      } catch (error) {
        debug.error('Error checking verification status:', error);
      }
      if (cancelled) return;
      if (Date.now() - startTime > EMAIL_VERIFICATION_POLL.DEADLINE_MS) {
        setPollingExpired(true);
        return;
      }
      timeoutId = setTimeout(check, EMAIL_VERIFICATION_POLL.INTERVAL_MS);
    };

    check();

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [status, memberUuid, pollingGeneration]);

  // Resend verification email mutation
  const resendMutation = useMutation({
    mutationFn: () => {
      if (!memberUuid) {
        throw new Error('Member UUID not available');
      }
      return signupApi.resendVerificationEmail({ member_uuid: memberUuid });
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Verification email sent! Please check your inbox.');
        setPollingExpired(false);
        setPollingGeneration((g) => g + 1);
      } else {
        toast.error(response.message || 'Failed to resend email');
      }
    },
    onError: () => {
      toast.error('Failed to resend verification email. Please try again.');
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (verifyToken: string) => signupApi.verifyEmail(verifyToken),
    onSuccess: (response) => {
      debug.log('Email verification response:', response);
      if (response.success) {
        setStatus('success');
        setMessage(response.message);

        // Store the member UUID if returned, but ONLY when there's no
        // existing authenticated session in this browser. If user A is
        // signed in and clicks user B's verification link (shared device,
        // forwarded email), overwriting memberUuid would leave a mismatched
        // (authToken_A, memberUuid_B) pair in localStorage — the axios
        // interceptor would keep using A's token while member-scoped reads
        // resolved B's UUID, driving the app off the wrong identity.
        if (response.member_uuid && !localStorage.getItem(SESSION_KEYS.AUTH_TOKEN)) {
          localStorage.setItem(SESSION_KEYS.MEMBER_UUID, response.member_uuid);
        }

        // Don't auto-redirect - let user navigate back to original window
        // The original signup/login window will detect verification via polling
      } else {
        setStatus('error');
        setMessage(response.message);
      }
    },
    onError: (error: unknown) => {
      debug.error('Email verification failed:', error);
      setStatus('error');
      const apiError = error as { response?: { data?: { detail?: string } } };
      const errorMessage =
        apiError.response?.data?.detail || 'Verification failed. Please try again.';
      setMessage(errorMessage);
    },
  });

  useEffect(() => {
    // Prevent duplicate verification attempts (e.g., from React Strict Mode double-mounting)
    if (verificationAttempted.current) {
      return;
    }

    if (token) {
      // Token provided - verify it
      verificationAttempted.current = true;
      verifyMutation.mutate(token);
    } else if (isFromEmailChange && pendingEmail && memberUuid) {
      // Coming from email change - show pending state
      verificationAttempted.current = true;
      setStatus('pending');
      setMessage(`We've sent a verification email to ${pendingEmail}. Please check your inbox and click the verification link.`);
    } else {
      setStatus('error');
      setMessage('No verification token provided. Please check your email link.');
    }
  }, [token, isFromEmailChange, pendingEmail, memberUuid, verifyMutation]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                <LoadingSpinner size="lg" variant="blue" label="Verifying email" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email</h2>
              <p className="text-gray-600">Please wait while we verify your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                <p className="text-sm text-blue-700">
                  {isFromEmailChange
                    ? 'Your new email address has been verified. You can now log in with your new email.'
                    : 'Please return to your Flouri window/tab to continue.'
                  }
                </p>
              </div>
              {isFromEmailChange && (
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 font-medium"
                >
                  Go to Login
                </button>
              )}
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                <svg
                  className="h-8 w-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your New Email</h2>
              <p className="text-gray-600 mb-4">
                We've sent a verification email to:
              </p>
              <p className="text-lg font-semibold text-blue-600 mb-4">{pendingEmail}</p>
              <p className="text-gray-600 text-sm mb-6">
                Please click the verification link in the email to confirm your new email address.
              </p>

              {/* Waiting indicator */}
              {pollingExpired ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 font-medium text-center">
                    We haven't detected verification yet.
                  </p>
                  <p className="text-xs text-amber-700 text-center mt-2">
                    Click "Resend Verification Email" below to get a new link, or check your spam folder.
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center">
                    <LoadingSpinner size="md" variant="blue" className="mr-3" label="Waiting for verification" />
                    <span className="text-blue-700 font-medium">Waiting for verification...</span>
                  </div>
                  <p className="text-xs text-blue-600 text-center mt-2">
                    This page will automatically update when you verify your email.
                  </p>
                </div>
              )}

              {/* Help text */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-2">Didn't receive the email?</h4>
                <ul className="text-sm text-gray-600 space-y-1 ml-5 list-disc">
                  <li>Check your spam or junk folder</li>
                  <li>Make sure you entered the correct email address</li>
                  <li>Wait a few minutes for the email to arrive</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => resendMutation.mutate()}
                  disabled={resendMutation.isPending}
                  className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {resendMutation.isPending ? 'Sending...' : 'Resend Verification Email'}
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
                >
                  Back to Login
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/signup')}
                  className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 font-medium"
                >
                  Back to Signup
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
                >
                  Go to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
